import React, { useState } from "react";
import { Drawer, Tag, Space, Typography, Empty, Segmented, Collapse, Table, Badge } from "antd";
import {
	ExperimentOutlined, BarcodeOutlined, FileTextOutlined,
	MedicineBoxOutlined, ScanOutlined, FormOutlined,
} from "@ant-design/icons";
import {
	BodyTag, TagCategory, TAG_SEVERITY_COLORS, TAG_CATEGORY_LABELS, BodyTagEvidence,
} from "../../../types/healthcare/BodyAnalysisTypes";

const { Text } = Typography;

const CATEGORY_ICONS: Record<TagCategory, React.ReactNode> = {
	blood:      <ExperimentOutlined />,
	genetic:    <BarcodeOutlined />,
	history:    <FileTextOutlined />,
	medication: <MedicineBoxOutlined />,
	imaging:    <ScanOutlined />,
	forms:      <FormOutlined />,
};

const SEVERITY_LABELS: Record<string, string> = {
	critical: "Critical", warning: "Warning", normal: "Normal",
};

const SEVERITY_ANT_COLOR: Record<string, string> = {
	critical: "error", warning: "warning", normal: "success",
};

const LAB_STATUS_COLOR: Record<string, string> = {
	normal: "success", borderline: "warning", abnormal: "error", critical: "error",
};

const ALL_FILTER = "All";
const CATEGORY_FILTER_OPTIONS: Array<{ label: string; value: string }> = [
	{ label: ALL_FILTER, value: ALL_FILTER },
	{ label: "Genetic",    value: "genetic" },
	{ label: "Blood",      value: "blood" },
	{ label: "History",    value: "history" },
	{ label: "Medication", value: "medication" },
	{ label: "Imaging",    value: "imaging" },
];

function hasNonEmptyEvidence(evidence: BodyTagEvidence | undefined): boolean {
	if (!evidence) return false;
	return (
		(evidence.genes?.length ?? 0) > 0 ||
		(evidence.lab_values?.length ?? 0) > 0 ||
		(evidence.conditions?.length ?? 0) > 0 ||
		(evidence.medications?.length ?? 0) > 0
	);
}

interface EvidencePanelProps {
	evidence: BodyTagEvidence;
}

const LAB_COLUMNS = [
	{ title: "Test",            dataIndex: "name",  key: "name" },
	{ title: "Value",           dataIndex: "value", key: "value" },
	{ title: "Reference Range", dataIndex: "range", key: "range" },
	{
		title: "Status",
		dataIndex: "status",
		key: "status",
		render: (status: string) => (
			<Badge
				status={LAB_STATUS_COLOR[status] as "success" | "warning" | "error" | "default"}
				text={<Text style={{ fontWeight: status === "critical" ? 700 : 400 }}>{status}</Text>}
			/>
		),
	},
];

const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidence }) => (
	<Space direction="vertical" size={10} style={{ width: "100%" }}>
		{(evidence.genes?.length ?? 0) > 0 && (
			<div>
				<Text strong style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Genes</Text>
				<Space wrap size={4}>
					{evidence.genes!.map((g) => (
						<Tag key={g} color="blue" style={{ fontSize: 11 }}>{g}</Tag>
					))}
				</Space>
			</div>
		)}
		{(evidence.lab_values?.length ?? 0) > 0 && (
			<div>
				<Text strong style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Lab Values</Text>
				<Table
					dataSource={evidence.lab_values}
					columns={LAB_COLUMNS}
					size="small"
					pagination={false}
					rowKey="name"
					style={{ fontSize: 11 }}
				/>
			</div>
		)}
		{(evidence.conditions?.length ?? 0) > 0 && (
			<div>
				<Text strong style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Linked Conditions</Text>
				<ul style={{ margin: 0, paddingLeft: 18 }}>
					{evidence.conditions!.map((c) => (
						<li key={c}><Text style={{ fontSize: 12 }}>{c}</Text></li>
					))}
				</ul>
			</div>
		)}
		{(evidence.medications?.length ?? 0) > 0 && (
			<div>
				<Text strong style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Linked Medications</Text>
				<ul style={{ margin: 0, paddingLeft: 18 }}>
					{evidence.medications!.map((m) => (
						<li key={m}><Text style={{ fontSize: 12 }}>{m}</Text></li>
					))}
				</ul>
			</div>
		)}
	</Space>
);

interface TagCardProps {
	tag: BodyTag;
}

const TagCard: React.FC<TagCardProps> = ({ tag }) => {
	const showEvidence = hasNonEmptyEvidence(tag.evidence);
	return (
		<div
			style={{
				border: `1px solid ${TAG_SEVERITY_COLORS[tag.severity]}40`,
				borderLeft: `4px solid ${TAG_SEVERITY_COLORS[tag.severity]}`,
				borderRadius: 8,
				padding: "12px 16px",
				background: `${TAG_SEVERITY_COLORS[tag.severity]}08`,
			}}
		>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
				<Space size={6}>
					<Tag
						icon={CATEGORY_ICONS[tag.category]}
						color="default"
						style={{ margin: 0, fontSize: 11 }}
					>
						{TAG_CATEGORY_LABELS[tag.category]}
					</Tag>
					<Tag
						color={SEVERITY_ANT_COLOR[tag.severity]}
						style={{ margin: 0, fontSize: 11 }}
					>
						{SEVERITY_LABELS[tag.severity]}
					</Tag>
				</Space>
			</div>
			<Text strong style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
				{tag.title}
			</Text>
			<Text type="secondary" style={{ fontSize: 12, display: "block" }}>
				{tag.detail}
			</Text>
			{tag.source && (
				<Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: "block", fontStyle: "italic" }}>
					Source: {tag.source}
				</Text>
			)}
			{showEvidence && (
				<Collapse
					size="small"
					style={{ marginTop: 10, background: "transparent", border: "none" }}
					items={[
						{
							key: "evidence",
							label: <Text style={{ fontSize: 12 }}>Scientific Evidence</Text>,
							children: <EvidencePanel evidence={tag.evidence!} />,
						},
					]}
				/>
			)}
		</div>
	);
};

interface BodyTagDrawerProps {
	open: boolean;
	label: string;
	tags: BodyTag[];
	onClose: () => void;
}

const BodyTagDrawer: React.FC<BodyTagDrawerProps> = ({ open, label, tags, onClose }) => {
	const [activeFilter, setActiveFilter] = useState<string>(ALL_FILTER);

	const countForCategory = (cat: string) =>
		cat === ALL_FILTER ? tags.length : tags.filter((t) => t.category === cat).length;

	const filteredTags = activeFilter === ALL_FILTER
		? tags
		: tags.filter((t) => t.category === activeFilter);

	const segmentedOptions = CATEGORY_FILTER_OPTIONS
		.filter(({ value }) => value === ALL_FILTER || countForCategory(value) > 0)
		.map(({ label: lbl, value }) => ({
			label: `${lbl}${value !== ALL_FILTER ? ` (${countForCategory(value)})` : ""}`,
			value,
		}));

	return (
		<Drawer
			title={
				<Space>
					<span style={{ fontSize: 16, fontWeight: 600 }}>{label}</span>
					<Text type="secondary" style={{ fontSize: 13 }}>
						{tags.length} finding{tags.length !== 1 ? "s" : ""}
					</Text>
				</Space>
			}
			open={open}
			onClose={onClose}
			width={440}
			placement="right"
		>
			{tags.length === 0 ? (
				<Empty description="No findings for this region" />
			) : (
				<Space direction="vertical" size={12} style={{ width: "100%" }}>
					{segmentedOptions.length > 1 && (
						<Segmented
							options={segmentedOptions}
							value={activeFilter}
							onChange={(val) => setActiveFilter(val as string)}
							size="small"
							style={{ width: "100%" }}
						/>
					)}
					{filteredTags.length === 0 ? (
						<Empty description="No findings match this filter" image={Empty.PRESENTED_IMAGE_SIMPLE} />
					) : (
						filteredTags.map((tag, i) => <TagCard key={i} tag={tag} />)
					)}
				</Space>
			)}
		</Drawer>
	);
};

export default BodyTagDrawer;
